import React from 'react';
import CalendarDayView from './CalendarDayView';
import { CalendarEventDisplay } from '../../utils/calendarUtils';

export interface CalendarAgendaViewProps {
  events: CalendarEventDisplay[];
  isPublic: boolean;
  scope?: 'all' | 'org' | 'ws';
  workspaceId?: string;
  onBillClick?: (billNumber: string) => void;
}

const formatGroupDateLabel = (dateStr: string) => {
  if (!dateStr || dateStr === 'unknown') return 'Unknown date';
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

const CalendarAgendaView: React.FC<CalendarAgendaViewProps> = ({
  events,
  isPublic,
  scope,
  workspaceId,
  onBillClick,
}) => {
  const grouped = React.useMemo(() => {
    const byDate = new Map<string, CalendarEventDisplay[]>();

    (events || []).forEach((event) => {
      const dateOnly = (event.reading_date || '').split('T')[0] || 'unknown';
      const existing = byDate.get(dateOnly) || [];
      existing.push(event);
      byDate.set(dateOnly, existing);
    });

    const sortedDates = Array.from(byDate.keys()).sort((a, b) => a.localeCompare(b));
    return sortedDates.map((date) => ({
      date,
      events: byDate.get(date) || [],
    }));
  }, [events]);

  return (
    <div className="space-y-6">
      {grouped.map((group) => (
        <div key={group.date} className="space-y-3">
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold text-text-primary dark:text-text-dark-primary whitespace-nowrap">
              {formatGroupDateLabel(group.date)}
            </span>
            <div className="w-full border-t border-border-light dark:border-border-dark" />
          </div>

          <CalendarDayView
            events={group.events}
            viewMode="card"
            isPublic={isPublic}
            scope={scope}
            workspaceId={workspaceId}
            onBillClick={onBillClick}
            showDateColumn={false}
          />
        </div>
      ))}
    </div>
  );
};

export default CalendarAgendaView;
